import { Button, Container, Icon, Pagination, Row, Table, usePagination } from '@zoom/components';
import { equals } from 'ramda';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flipper } from 'react-flip-toolkit';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { RealTimeAdherenceBottomPanel } from './RealTimeAdherenceBottomPanel';
import {
  RealTimeAdherenceFilterSidebar,
  RealTimeAdherenceFilterSidebarState,
  defaultRealTimeAdherenceFilterSidebar,
} from './RealTimeAdherenceFilterSidebar';
import {
  RealTimeAdherenceHeaderFilterType,
  RealTimeAdherenceHeaderSearchForm,
} from './RealTimeAdherenceHeaderSearchForm';
import { RealTimeAdherenceRow } from './RealTimeAdherenceRow';
import {
  CONTAINER_TAG,
  LIST_CONTAINER_TAG,
  LIST_PAGINATION_TAG,
  TITLE_TAG,
  TOGGLE_FILTER_BUTTON_TAG,
  TRANSITION_DELAY_TIMEOUT,
  TRANSITION_OUT_DELAY_TIMEOUT,
  getListHeaderTag,
} from './constants';
import { AgentIdsState, AgentOverview, AgentOverviewWithTeams, SceneTransitions } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { ActiveFilters as ActiveFiltersBase } from '../../components/ActiveFilters/ActiveFilters';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { useBottomPanel } from '../../components/layout/BottomPanel';
import { Content } from '../../components/layout/Content';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  RealTimeAdherenceState,
  Team,
  useGetRealTimeAdherenceAgentOverviewsQuery,
  useGetSupervisedTeamsQuery,
  useGetTeamsAllQuery,
  useRealTimeAdherenceChangedSubscription,
} from '../../graphql/components';
import { SupervisedTeamsResult } from '../../graphql/types';
import { PATH_REAL_TIME_ADHERENCE } from '../../router/routes';
import { SortOrder } from '../../types';
import { recordBy } from '../../utils/array';
import { useFilter } from '../../utils/filter';
import { formatNameOfPerson, nameOf } from '../../utils/helpers';
import { customSort } from '../../utils/sort';
import { ONE_SECOND } from '../../utils/time';

export const SYNC_TIMEOUT = 50;

export const SORT_IN_ADHERENCE_AGENTS_OVERVIEWS_BY_FIELDS: Array<keyof AgentOverviewWithTeams> = [
  'lastName',
  'firstName',
];
export const SORT_OUT_OF_ADHERENCE_AGENTS_OVERVIEWS_BY_FIELDS: Array<keyof AgentOverviewWithTeams> = [
  'inStateSinceTime',
  'lastName',
  'firstName',
];

const defaultQueryData = {
  totalCount: 0,
};

const defaultTransitionsState = {
  agents: {},
  scheduled: [],
};

export const RealTimeAdherenceScene: React.FC = () => {
  const { userId } = useAuth();

  const { t } = useTranslation();
  const { closeBottomPanel, isBottomPanelOpen, openBottomPanel } = useBottomPanel();

  const { filter, setFilter, isFilterActive } = useFilter<RealTimeAdherenceHeaderFilterType>();
  const { selectedAgentId } = useParams<{ selectedAgentId?: string }>();
  const routerHistory = useHistory();

  const [agentsOverviews, setAgentsOverviews] = useState<Record<string, AgentOverviewWithTeams>>({});

  // To store all available agents with correct teamIds property as all next subscription data will not includes teamIds information
  const [initialAllAgentsOverviews, setInitialAllAgentsOverviews] = useState<Record<
    string,
    AgentOverviewWithTeams
  > | null>(null);

  const [agentIds, setAgentIds] = useState<AgentIdsState>({
    all: [],
    inAdherence: [],
    outOfAdherence: [],
    visible: [],
  });

  const sceneTransitions = useRef<SceneTransitions>(defaultTransitionsState);

  const [queryOptions, setQueryOptions] = useState(defaultQueryData);
  const [agentIdToView, setAgentIdToView] = useState<string | undefined>();
  const [timeStamp, setTimeStamp] = useState(new Date().getTime());

  const { activePage, itemsPerPage: limit, offset, setPage, totalPages } = usePagination(queryOptions.totalCount);

  const { isSidebarOpen, closeSidebar, openSidebar } = useSidebar();

  const { filter: sidebarFilter, setFilter: setSidebarFilter } = useFilter<RealTimeAdherenceFilterSidebarState>(
    defaultRealTimeAdherenceFilterSidebar
  );

  // Sidebar Filter Accordion open tiles state
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  const getSearchedAgentIds = useCallback(
    (agentIdsToSearchIn: string[]) => {
      let searchedAgentIds = agentIdsToSearchIn;

      if (filter.name) {
        searchedAgentIds = agentIdsToSearchIn.filter((agentId) => {
          const agentName = formatNameOfPerson(
            agentsOverviews[agentId].firstName,
            agentsOverviews[agentId].lastName
          ).toLowerCase();

          return agentName.includes(filter.name as string);
        });
      }

      return searchedAgentIds;
    },
    [agentsOverviews, filter.name]
  );

  useEffect(() => {
    const updatedFilterIds = getSearchedAgentIds(agentIds.all);

    // need to update visible agentIds
    setAgentIds((state) => {
      return {
        ...state,
        visible: (filter.name ? updatedFilterIds : state.all).slice(0, 0 + limit),
        filtered: filter.name ? updatedFilterIds : undefined,
      };
    });

    // need to update total count of visible agents after name filter is use to be properly handled by pagination
    setQueryOptions({
      totalCount: (filter.name ? updatedFilterIds : agentIds.all).length,
    });

    setPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.name]);

  useEffect(() => {
    // need to update visible agentIds
    setAgentIds((state) => {
      return {
        ...state,
        visible: (state.filtered ?? state.all).slice(offset, offset + limit),
      };
    });
  }, [activePage, limit, offset]);

  const updateAgentOverview = useCallback(
    (agentOverview: AgentOverview) => {
      const { agentId, state } = agentOverview;

      let isAgentWithSuitableTeams = true;

      const isTeamFilterActive = Object.keys(sidebarFilter.teams).length > 0;

      if (isTeamFilterActive) {
        // Get teamIds information from initial load
        const updatedAgentTeamIds = initialAllAgentsOverviews ? initialAllAgentsOverviews[agentId]?.teamIds : null;

        isAgentWithSuitableTeams = updatedAgentTeamIds
          ? updatedAgentTeamIds.some((updatedAgentTeamId) => sidebarFilter.teams[updatedAgentTeamId])
          : false;
      }

      const sortTransitionTimestamp = timeStamp + TRANSITION_DELAY_TIMEOUT;

      // Only update data of agent who suits current filter teams criteria will be processed
      if (isAgentWithSuitableTeams) {
        // needs to be optional as agent's overview may not have come from initial load...
        if (agentsOverviews[agentId]?.state !== state) {
          sceneTransitions.current.agents[agentId] = {
            timeStamp: sortTransitionTimestamp,
            type: 'start',
          };
        }

        if (!sceneTransitions.current.scheduled.includes(sortTransitionTimestamp)) {
          sceneTransitions.current.scheduled = [...sceneTransitions.current.scheduled, sortTransitionTimestamp].sort();
        }

        // overview should be updated even when state has not changed
        setAgentsOverviews((currentState) => ({
          ...currentState,
          [agentId]: { ...agentOverview, teamIds: currentState[agentId]?.teamIds }, // As teamIds info is not provided in RealTimeAdherenceChangedSubscription object need to be reused
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeStamp, agentsOverviews]
  );

  const {
    data: agentOverviewsData,
    loading: agentOverviewsLoading,
    error: agentOverviewsError,
  } = useGetRealTimeAdherenceAgentOverviewsQuery({
    variables: {
      teamIds: Object.keys(sidebarFilter.teams)?.length ? Object.keys(sidebarFilter.teams)?.toString() : undefined,
    },
    fetchPolicy: 'no-cache',
    onCompleted: ({ agentOverviews }) => {
      // Reseting sceneTransitions once new data was loaded for example after new teams filter submit
      sceneTransitions.current = defaultTransitionsState;

      if (agentOverviews?.length) {
        // data should be already sorted
        const inAdherenceIds: string[] = [];
        const outOfAdherenceIds: string[] = [];

        const newAgentsOverviews: Record<string, AgentOverviewWithTeams> = {};

        agentOverviews.forEach((agentOverview) => {
          const { agent, inStateSince, ...restOverview } = agentOverview;
          const { agentId } = agent;

          newAgentsOverviews[agentId] = {
            ...agent,
            ...restOverview,
            inStateSinceTime: new Date(inStateSince).getTime(),
          };

          if (agentOverview.state === RealTimeAdherenceState.In) {
            inAdherenceIds.push(agentId);
          } else {
            outOfAdherenceIds.push(agentId);
          }
        });

        const allAgentIdsSorted = outOfAdherenceIds.concat(inAdherenceIds);

        if (initialAllAgentsOverviews === null) {
          setInitialAllAgentsOverviews(newAgentsOverviews);
        }

        setAgentsOverviews(newAgentsOverviews);
        setAgentIds({
          inAdherence: inAdherenceIds,
          outOfAdherence: outOfAdherenceIds,
          all: allAgentIdsSorted,
          visible: allAgentIdsSorted.slice(offset, offset + limit),
        });
      }

      setQueryOptions({
        totalCount: agentOverviews?.length ?? 0,
      });
    },
  });

  useRealTimeAdherenceChangedSubscription({
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.realTimeAdherenceChanged) {
        const { agent, inStateSince, ...restOverview } = subscriptionData.data?.realTimeAdherenceChanged ?? {};
        updateAgentOverview({
          ...restOverview,
          ...agent,
          inStateSinceTime: new Date(inStateSince).getTime(),
        });
      }
    },
  });

  const {
    data: teamsData,
    loading: teamsLoading,
    error: teamsError,
  } = useGetSupervisedTeamsQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
    },
  });

  const {
    data: teamsAllData,
    loading: teamsAllLoading,
    error: teamsAllError,
  } = useGetTeamsAllQuery({
    fetchPolicy: 'network-only',
  });

  const loading = agentOverviewsLoading || teamsLoading || teamsAllLoading;
  const error = agentOverviewsError || teamsError || teamsAllError;

  // All teams used in table view
  const teamsAll: Record<string, Team> = useMemo(
    () => recordBy(teamsAllData?.teams ?? [], 'id'),
    [teamsAllData?.teams]
  );

  const handleCloseAgentOverviewToView = useCallback(
    () => routerHistory.push(PATH_REAL_TIME_ADHERENCE),
    [routerHistory]
  );

  const handlePrepareView = useCallback(
    (agentId: string) => {
      routerHistory.push(`${PATH_REAL_TIME_ADHERENCE}/${agentId}`);
    },
    [routerHistory]
  );

  useEffect(() => {
    if (loading || error || !agentIds.all.length) {
      return;
    }

    if (isSidebarOpen) {
      handleCloseAgentOverviewToView();
    }

    if (!selectedAgentId && isBottomPanelOpen) {
      setAgentIdToView(undefined);
      closeBottomPanel();
      return;
    }

    if (!selectedAgentId) {
      setAgentIdToView(undefined);
      return;
    }

    if (queryOptions.totalCount === 0) {
      // pagination is not aware of correct agents' overviews amount yet
      return;
    }

    if (selectedAgentId !== agentIdToView) {
      const agentIdIndex = (agentIds.filtered ?? agentIds.all).indexOf(selectedAgentId);

      if (agentIdIndex !== -1) {
        const agentPage = Math.floor(agentIdIndex / limit) + 1;
        if (activePage !== agentPage) {
          setPage(agentPage);
        }

        setAgentIdToView(selectedAgentId);
        openBottomPanel();
        return;
      }

      routerHistory.push(PATH_REAL_TIME_ADHERENCE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId, agentIds, routerHistory, loading, error, queryOptions, isSidebarOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      const date = new Date();
      const millisecondsOffset = date.getMilliseconds();
      const timestamp = date.getTime();
      const timeFromLastUpdate = timestamp - (timeStamp || 0);

      // skip update when it is not first 50ms of second and previous update was not within 1s
      if (millisecondsOffset > SYNC_TIMEOUT && timeFromLastUpdate < ONE_SECOND) {
        return;
      }

      setTimeStamp(timestamp - millisecondsOffset);
    }, SYNC_TIMEOUT);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAgentsTransitionState = useCallback(() => {
    let shouldAddRemovalOfTransitionsIntoSchedule = false;
    const endOfRemovalTransitionTimeStamp = timeStamp + TRANSITION_OUT_DELAY_TIMEOUT;

    const newInAdherenceIds: string[] = [];
    const newOutOfAdherenceIds: string[] = [];

    Object.keys(sceneTransitions.current.agents).forEach((agentId) => {
      const { timeStamp: transitionTimeStamp, type } = sceneTransitions.current.agents[agentId];
      if (transitionTimeStamp > timeStamp) {
        return; // skip current iteration
      }

      if (type === 'end' || !agentsOverviews[agentId]) {
        // transition is completed (or agent is no longer present in current data/view), cleanup
        delete sceneTransitions.current.agents[agentId];
      } else {
        // time to resort agent
        const { state } = agentsOverviews[agentId];

        if (state === RealTimeAdherenceState.In) {
          newInAdherenceIds.push(agentId);
        } else {
          newOutOfAdherenceIds.push(agentId);
        }

        shouldAddRemovalOfTransitionsIntoSchedule = true;

        sceneTransitions.current.agents[agentId] = {
          timeStamp: endOfRemovalTransitionTimeStamp,
          type: 'end',
        };
      }
    });

    if (newInAdherenceIds.length || newOutOfAdherenceIds.length) {
      let updatedInAdherenceIds = agentIds.inAdherence.filter((agentId) => !newOutOfAdherenceIds.includes(agentId));
      let updatedOutOfAdherenceIds = agentIds.outOfAdherence.filter((agentId) => !newInAdherenceIds.includes(agentId));

      if (newInAdherenceIds) {
        // removing possible duplicities
        updatedInAdherenceIds = Array.from(new Set([...updatedInAdherenceIds, ...newInAdherenceIds]));

        // need to resort
        updatedInAdherenceIds = customSort(
          agentsOverviews,
          SORT_IN_ADHERENCE_AGENTS_OVERVIEWS_BY_FIELDS,
          SortOrder.ASCENDING,
          updatedInAdherenceIds
        );
      }

      if (newOutOfAdherenceIds) {
        // removing possible duplicities
        updatedOutOfAdherenceIds = Array.from(new Set([...updatedOutOfAdherenceIds, ...newOutOfAdherenceIds]));

        // need to resort
        updatedOutOfAdherenceIds = customSort(
          agentsOverviews,
          SORT_OUT_OF_ADHERENCE_AGENTS_OVERVIEWS_BY_FIELDS,
          SortOrder.ASCENDING,
          updatedOutOfAdherenceIds
        );
      }

      const updatedAllIds = updatedOutOfAdherenceIds.concat(updatedInAdherenceIds);
      const updatedFilterIds = getSearchedAgentIds(updatedAllIds);

      setAgentIds({
        inAdherence: updatedInAdherenceIds,
        outOfAdherence: updatedOutOfAdherenceIds,
        all: updatedAllIds,
        visible: (filter.name ? updatedFilterIds : updatedAllIds).slice(offset, offset + limit),
        filtered: filter.name ? updatedFilterIds : undefined,
      });
    }

    const upcomingScheduledTransitions = sceneTransitions.current.scheduled.filter(
      (scheduledTransition) => scheduledTransition > timeStamp
    );

    sceneTransitions.current.scheduled = shouldAddRemovalOfTransitionsIntoSchedule
      ? [...upcomingScheduledTransitions, endOfRemovalTransitionTimeStamp].sort()
      : upcomingScheduledTransitions;
  }, [
    agentsOverviews,
    agentIds.inAdherence,
    agentIds.outOfAdherence,
    limit,
    offset,
    timeStamp,
    getSearchedAgentIds,
    filter.name,
  ]);

  useEffect(() => {
    if (sceneTransitions.current.scheduled.length && sceneTransitions.current.scheduled[0] < timeStamp) {
      updateAgentsTransitionState();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp]);

  // required for react-flip-toolkit to determine that the Flipper needs to update position of Flipped elements
  const flipKey = useMemo(() => agentIds.visible.join(','), [agentIds.visible]);

  const onFilterSubmit = ({ name }: RealTimeAdherenceHeaderFilterType) => {
    const values = {
      name: name?.trim().toLowerCase(),
    };

    setFilter(values);
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    setPreOpenedItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleFilterSidebar = () => {
    setPreOpenedItemIds([]);

    if (isSidebarOpen) {
      closeSidebar();
    } else {
      openSidebar();
    }
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setPreOpenedItemIds([]);
  };

  const ActiveFilters: React.FC<{ isFixed?: boolean }> = ({ isFixed }) => (
    <ActiveFiltersBase
      config={[
        {
          dataTestId: 'real-time-adherence-sidebar-filter-teams',
          defaultValue: {},
          id: '1',
          key: 'teams',
          label: t('team.team', { count: 2 }),
        },
      ]}
      isFixed={isFixed}
      sidebarFilter={sidebarFilter}
      setSidebarFilter={setSidebarFilter}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      cancelFilterSidebar={handleCancelFilterSidebar}
    />
  );

  return (
    <Content
      header={{
        actionBar: (
          <Row>
            <RealTimeAdherenceHeaderSearchForm onSubmit={onFilterSubmit} />

            <div className="ml-auto">
              <Button
                active={isSidebarOpen}
                className="button--icon--left-hand-side ml-auto"
                color="info"
                data-test-id={TOGGLE_FILTER_BUTTON_TAG}
                onClick={handleFilterSidebar}
              >
                <Icon color="#239ed8" kind="bars" size={20} />
                {t('filter.filters')}
              </Button>
            </div>
          </Row>
        ),
        testId: TITLE_TAG,
        title: t('scheduleAdherence.realTimeAdherence.realTimeAdherence'),
        titleSlug: 'real-time-adherence',
      }}
      bottomPanelContent={
        agentIdToView && (
          <RealTimeAdherenceBottomPanel
            agentOverviewItem={agentsOverviews[agentIdToView]}
            onClose={handleCloseAgentOverviewToView}
            timeStamp={timeStamp}
            teams={teamsAll}
          />
        )
      }
      sidebarContent={
        <RealTimeAdherenceFilterSidebar
          closeSidebar={closeSidebar}
          preOpenedItemIds={preOpenedItemIds}
          teamsResult={{ data: teamsData } as SupervisedTeamsResult}
          setPreOpenedItemIds={setPreOpenedItemIds}
          setSidebarFilter={setSidebarFilter}
          sidebarFilter={sidebarFilter}
        />
      }
    >
      <Container data-test-id={CONTAINER_TAG}>
        <LoadingOverlay loading={loading}>
          {(() => {
            if ((loading && !agentOverviewsData) || error) {
              return null;
            }

            if (
              !agentOverviewsData ||
              agentOverviewsData.agentOverviews.length === 0 ||
              agentIds.visible.length === 0
            ) {
              return (
                <>
                  <ActiveFilters isFixed />
                  <FullSizeMessage
                    headline={t(
                      isFilterActive || !equals(defaultRealTimeAdherenceFilterSidebar, sidebarFilter)
                        ? 'filter.noResultsMatch'
                        : 'scheduleAdherence.realTimeAdherence.noEntries'
                    )}
                    iconKind="no_data"
                  />
                </>
              );
            }

            return (
              <>
                <ActiveFilters />
                <Table
                  className="real-time-adherence__table"
                  data-test-id={LIST_CONTAINER_TAG}
                  striped
                  fixedLayout
                  withContainerPadding
                >
                  <thead>
                    <tr>
                      <th data-test-id={getListHeaderTag('name')}>{t('person.name')}</th>
                      <th className="text-center" data-test-id={getListHeaderTag('teamIds')}>
                        {t('team.team', { count: 2 })}
                      </th>
                      <th
                        className="text-center"
                        data-test-id={getListHeaderTag(nameOf<AgentOverview>('currentActivity'))}
                      >
                        {t('scheduleAdherence.realTimeAdherence.currentStatus')}
                      </th>
                      <th
                        className="text-center"
                        data-test-id={getListHeaderTag(nameOf<AgentOverview>('expectedActivity'))}
                      >
                        {t('scheduleAdherence.realTimeAdherence.scheduledActivity')}
                      </th>
                      <th className="text-center" data-test-id={getListHeaderTag(nameOf<AgentOverview>('state'))}>
                        {t('scheduleAdherence.inAdherence')}
                      </th>
                      <th
                        className="text-center"
                        data-test-id={getListHeaderTag(nameOf<AgentOverview>('inStateSinceTime'))}
                      >
                        {t('scheduleAdherence.realTimeAdherence.ooaSince')}
                      </th>
                      <th className="text-center" data-test-id={getListHeaderTag('timeOutOfAdherence')}>
                        {t('time.duration')}
                      </th>
                      <th className="actions" data-test-id={getListHeaderTag('actions')}>
                        {t('layout.action.actions')}
                      </th>
                    </tr>
                  </thead>

                  <Flipper flipKey={flipKey} decisionData={agentIds.visible} element="tbody">
                    {agentIds.visible.map((agentId, i) => (
                      <RealTimeAdherenceRow
                        agentOverview={agentsOverviews[agentId]}
                        key={agentId}
                        index={i}
                        isDetailView={agentIdToView === agentId}
                        onDetailView={handlePrepareView}
                        sceneTransition={sceneTransitions.current.agents[agentId]}
                        timeStamp={timeStamp}
                        teams={teamsAll}
                      />
                    ))}
                  </Flipper>
                </Table>

                <Pagination
                  activePage={activePage}
                  buttons={{
                    first: t('layout.pagination.first'),
                    last: t('layout.pagination.last'),
                    next: t('layout.pagination.next'),
                    previous: t('layout.pagination.previous'),
                  }}
                  dataTestId={LIST_PAGINATION_TAG}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </>
            );
          })()}
        </LoadingOverlay>
      </Container>
    </Content>
  );
};
